import { useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { experimentalStyled as styled } from "@material-ui/core/styles";
import {
  Container,
  Typography,
  TextField,
  Box,
  InputAdornment,
  IconButton,
  Alert,
} from "@material-ui/core";
import LoadingButton from "@material-ui/lab/LoadingButton";
import LockOutlinedIcon from "@material-ui/icons/LockOutlined";
import VisibilityIcon from "@material-ui/icons/Visibility";
import VisibilityOffIcon from "@material-ui/icons/VisibilityOff";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import Page from "src/components/Page";
import { MHidden } from "src/components/@material-extend";
import logoBlanco from "src/assets/logo/logo_blanco.png";
import logo from "src/assets/logo/logo.png";
import { useFormik, Form, FormikProvider } from "formik";
import * as Yup from "yup";
import { useConfirmarResetPortalMutation } from "src/services/loginApi";

// ── Layout idéntico a Login.js ─────────────────────────────────────────────

const RootStyle = styled(Page)(({ theme }) => ({
  [theme.breakpoints.up("md")]: {
    display: "flex",
  },
}));

const SectionStyle = styled("div")(() => ({
  width: "100%",
  maxWidth: 464,
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
  backgroundColor: "#212B36",
  padding: "48px 32px",
  minHeight: "100vh",
}));

const ContentStyle = styled("div")(({ theme }) => ({
  maxWidth: 480,
  margin: "auto",
  display: "flex",
  minHeight: "100vh",
  flexDirection: "column",
  justifyContent: "center",
  padding: theme.spacing(12, 0),
}));

// ──────────────────────────────────────────────────────────────────────────

const Schema = Yup.object().shape({
  nuevaContrasena: Yup.string()
    .min(8, "La contraseña debe tener al menos 8 caracteres")
    .required("La contraseña es requerida"),
  confirmarContrasena: Yup.string()
    .oneOf([Yup.ref("nuevaContrasena")], "Las contraseñas no coinciden")
    .required("Debes confirmar la contraseña"),
});

export default function ResetPasswordCliente() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token");

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [error, setError] = useState("");
  const [exito, setExito] = useState(false);

  const [confirmarResetPortal] = useConfirmarResetPortalMutation();

  const formik = useFormik({
    initialValues: { nuevaContrasena: "", confirmarContrasena: "" },
    validationSchema: Schema,
    onSubmit: async (_, { setSubmitting }) => {
      setError("");
      try {
        await confirmarResetPortal({
          token,
          nuevaContrasena: formik.values.nuevaContrasena,
        }).unwrap();
        setExito(true);
      } catch (err) {
        setError(
          err?.data?.message ||
            "El enlace es inválido o ha expirado. Solicita uno nuevo desde la pantalla de inicio de sesión.",
        );
      } finally {
        setSubmitting(false);
      }
    },
  });

  const { errors, touched, isSubmitting, handleSubmit, getFieldProps } = formik;

  // Token ausente en la URL — error inmediato, sin formulario
  if (!token) {
    return (
      <RootStyle title="Restablecer contraseña — Taxtic">
        <MHidden width="mdDown">
          <SectionStyle>
            <img
              src={logoBlanco}
              alt="Taxtic"
              style={{ width: 160, marginBottom: 24 }}
            />
            <Typography
              variant="subtitle1"
              style={{ color: "#919EAB", letterSpacing: 1 }}
            >
              Portal Taxtic
            </Typography>
            <Typography
              variant="body2"
              style={{ color: "#637381", marginTop: 8 }}
            >
              Asesoría Tributaria Integral
            </Typography>
          </SectionStyle>
        </MHidden>
        <Container maxWidth="sm">
          <ContentStyle>
            <MHidden width="mdUp">
              <Box display="flex" justifyContent="center" mb={4}>
                <img src={logo} alt="Taxtic" style={{ width: 140 }} />
              </Box>
            </MHidden>
            <Alert severity="error">
              El enlace de restablecimiento no es válido. Solicita uno nuevo
              desde la pantalla de inicio de sesión.
            </Alert>
            <Box mt={3}>
              <LoadingButton
                fullWidth
                variant="contained"
                color="primary"
                onClick={() => navigate("/login")}
              >
                Volver al inicio de sesión
              </LoadingButton>
            </Box>
          </ContentStyle>
        </Container>
      </RootStyle>
    );
  }

  return (
    <RootStyle title="Restablecer contraseña — Taxtic">
      <MHidden width="mdDown">
        <SectionStyle>
          <img
            src={logoBlanco}
            alt="Taxtic"
            style={{ width: 160, marginBottom: 24 }}
          />
          <Typography
            variant="subtitle1"
            style={{ color: "#919EAB", letterSpacing: 1 }}
          >
            Portal Taxtic
          </Typography>
          <Typography
            variant="body2"
            style={{ color: "#637381", marginTop: 8 }}
          >
            Asesoría Tributaria Integral
          </Typography>
        </SectionStyle>
      </MHidden>

      <Container maxWidth="sm">
        <ContentStyle>
          <MHidden width="mdUp">
            <Box display="flex" justifyContent="center" mb={4}>
              <img src={logo} alt="Taxtic" style={{ width: 140 }} />
            </Box>
          </MHidden>

          <Box
            display="flex"
            alignItems="center"
            style={{ gap: 10, marginBottom: 8 }}
          >
            <LockOutlinedIcon color="primary" style={{ fontSize: 28 }} />
            <Typography variant="h4">Restablecer contraseña</Typography>
          </Box>

          {/* ── Estado: éxito ────────────────────────────────────────────── */}
          {exito ? (
            <Box mt={2}>
              <Box
                display="flex"
                alignItems="center"
                style={{ gap: 8, marginBottom: 16 }}
              >
                <CheckCircleOutlineIcon
                  style={{ color: "#2e7d32", fontSize: 28 }}
                />
                <Typography variant="body1" style={{ color: "#2e7d32" }}>
                  Contraseña actualizada correctamente.
                </Typography>
              </Box>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                Ya puedes iniciar sesión con tu nueva contraseña.
              </Typography>
              <Box mt={3}>
                <LoadingButton
                  fullWidth
                  variant="contained"
                  color="primary"
                  onClick={() => navigate("/login")}
                >
                  Ir al inicio de sesión
                </LoadingButton>
              </Box>
            </Box>
          ) : (
            /* ── Estado: formulario ───────────────────────────────────────── */
            <>
              <Typography
                variant="body2"
                color="textSecondary"
                style={{ marginBottom: 24 }}
              >
                Ingresa tu nueva contraseña. Debe tener al menos 8 caracteres.
              </Typography>

              <FormikProvider value={formik}>
                <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
                  <TextField
                    fullWidth
                    label="Nueva contraseña"
                    type={showPassword ? "text" : "password"}
                    margin="normal"
                    {...getFieldProps("nuevaContrasena")}
                    error={Boolean(
                      touched.nuevaContrasena && errors.nuevaContrasena,
                    )}
                    helperText={
                      touched.nuevaContrasena && errors.nuevaContrasena
                    }
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowPassword((v) => !v)}
                            edge="end"
                          >
                            {showPassword ? (
                              <VisibilityIcon />
                            ) : (
                              <VisibilityOffIcon />
                            )}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />

                  <TextField
                    fullWidth
                    label="Confirmar contraseña"
                    type={showConfirm ? "text" : "password"}
                    margin="normal"
                    {...getFieldProps("confirmarContrasena")}
                    error={Boolean(
                      touched.confirmarContrasena && errors.confirmarContrasena,
                    )}
                    helperText={
                      touched.confirmarContrasena && errors.confirmarContrasena
                    }
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowConfirm((v) => !v)}
                            edge="end"
                          >
                            {showConfirm ? (
                              <VisibilityIcon />
                            ) : (
                              <VisibilityOffIcon />
                            )}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />

                  {error && (
                    <Alert severity="error" style={{ marginTop: 16 }}>
                      {error}
                    </Alert>
                  )}

                  <Box mt={3}>
                    <LoadingButton
                      fullWidth
                      type="submit"
                      variant="contained"
                      color="primary"
                      loading={isSubmitting}
                    >
                      Guardar nueva contraseña
                    </LoadingButton>
                  </Box>
                </Form>
              </FormikProvider>
            </>
          )}
        </ContentStyle>
      </Container>
    </RootStyle>
  );
}
